import React, { useState, useEffect, Component } from 'react';
import { connect } from 'react-redux';
import ApiService from '../../../../services/apiService';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { FaEdit } from 'react-icons/fa';
import { MdOutlineEditOff } from 'react-icons/md';

// ─── BtnCellRenderer ────────────────────────────────────────────────────────
class BtnCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.btnClickedHandler = this.btnClickedHandler.bind(this);
    this.btnEditClickedHandler = this.btnEditClickedHandler.bind(this);
  }

  btnEditClickedHandler() {
    const { data, colDef } = this.props;
    if (colDef?.cellRendererParams?.onEdit) {
      colDef.cellRendererParams.onEdit(data);
    }
  }

  btnClickedHandler() {
    const { value, colDef, data } = this.props;
    if (colDef?.cellRendererParams?.clicked) {
      colDef.cellRendererParams.clicked(value, !data.isActive);
    }
  }

  render() {
    const { isActive } = this.props.data;
    const { canEditMaster } = this.props.colDef?.cellRendererParams || {};

    return (
      <div className="d-flex gap-3 align-items-center">
        {canEditMaster ? (
          <div style={{ cursor: 'pointer' }} onClick={this.btnEditClickedHandler}>
            <FaEdit size={20} />
          </div>
        ) : (
          <div>
            <MdOutlineEditOff size={20} />
          </div>
        )}
        <div className="form-check form-switch mb-0">
          <input className="form-check-input" type="checkbox" role="switch" checked={!!isActive} disabled={!canEditMaster} onChange={this.btnClickedHandler} />
        </div>
      </div>
    );
  }
}

// ─── BrandMasterGrid ────────────────────────────────────────────────────────
const BrandMasterGrid = ({ rowData, canEditMaster, onEditBrand, fetchBrandData }) => {
  const updateActiveStatus = async (id, isActive) => {
    try {
      const apiService = new ApiService();
      const res = await apiService.put('UPDATE_BRANDS', { id, isActive });
      if (res?.status === 200) {
        toast.success('Status updated');
        fetchBrandData();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  const columnDefs = [
    {
      headerName: 'Brand Name',
      field: 'name',
      filter: true,
      floatingFilter: true,
      flex: 2,
      minWidth: 180,
    },
    {
      headerName: 'Brand Code',
      field: 'code',
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 140,
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 140,
    },
    {
      headerName: 'Created At',
      field: 'createdAt',
      filter: true,
      floatingFilter: true,
      flex: 2,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return '';
        try {
          return new Date(params.value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch {
          return params.value;
        }
      },
    },
    {
      headerName: 'Status',
      field: 'isActive',
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => (params.value ? 'Active' : 'Inactive'),
      cellStyle: (params) => ({
        color: params.value ? '#28a745' : '#dc3545',
        fontWeight: '600',
      }),
    },
    {
      headerName: 'Actions',
      field: 'id',
      flex: 1,
      minWidth: 120,
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        canEditMaster,
        onEdit: onEditBrand,
        clicked: (id, isActive) => updateActiveStatus(id, isActive),
      },
      sortable: false,
      filter: false,
    },
  ];

  return (
    <div className="ag-theme-quartz" style={{ height: 500, width: '100%' }}>
      <AgGridReact
        rowData={rowData || []}
        columnDefs={columnDefs}
        defaultColDef={{ resizable: true, sortable: true }}
        pagination={true}
        paginationPageSize={20}
        suppressExcelExport={true}
      />
    </div>
  );
};

// ─── BrandMaster (main) ──────────────────────────────────────────────────────
const BrandMaster = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [editBrand, setEditBrand] = useState(location.state || null);
  const [brands, setBrands] = useState([]);
  const [canAdd] = useState(true); // wire to Redux permissions as needed
  const [canEdit] = useState(true); // wire to Redux permissions as needed

  const [formData, setFormData] = useState({
    id: null,
    name: '',
    code: '',
    isActive: true,
    createdBy: '',
  });

  // Initial fetch
  useEffect(() => {
    getBrands();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editBrand) {
      setFormData({
        id: editBrand.id,
        name: editBrand.name || '',
        code: editBrand.code || '',
        isActive: editBrand.isActive ?? true,
        createdBy: editBrand.createdBy || '',
      });
    }
  }, [editBrand]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (editBrand && !canEdit) {
      toast.error('You do not have permission to edit Brand.');
      return;
    }
    if (!editBrand && !canAdd) {
      toast.error('You do not have permission to add Brand.');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Brand Name is required.');
      return;
    }
    if (!formData.code.trim()) {
      toast.error('Brand Code is required.');
      return;
    }

    try {
      const apiService = new ApiService();

      if (editBrand) {
        // UPDATE — send id from GET response
        const payload = {
          id: formData.id,
          name: formData.name,
          code: formData.code,
          isActive: formData.isActive,
          createdBy: formData.createdBy,
        };
        const response = await apiService.put('UPDATE_BRANDS', payload);
        if (response?.status === 200) {
          toast.success(response.data?.message || 'Brand updated successfully');
          resetFormData();
          getBrands();
        } else {
          toast.error('Failed to update Brand');
        }
      } else {
        // CREATE
        const payload = {
          name: formData.name,
          code: formData.code,
          isActive: formData.isActive,
          createdBy: formData.createdBy,
        };
        const response = await apiService.post('CREATE_BRANDS', payload);
        if (response?.status === 200 || response?.status === 201) {
          toast.success(response.data?.message || 'Brand created successfully');
          resetFormData();
          getBrands();
        } else {
          toast.error('Failed to create Brand');
        }
      }
    } catch (error) {
      console.error('Error saving Brand:', error);
      toast.error('Error saving Brand');
    }
  };

  const resetFormData = () => {
    setFormData({ id: null, name: '', code: '', isActive: true, createdBy: '' });
    setEditBrand(null);
  };

  const getBrands = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANDS');
      setBrands(res?.data?.result || []);
    } catch (error) {
      console.error('Error fetching Brand data:', error);
      toast.error('Error fetching Brand data');
    }
  };

  const handleEditBrand = (brand) => {
    setEditBrand(brand);
    setFormData({
      id: brand.id,
      name: brand.name || '',
      code: brand.code || '',
      isActive: brand.isActive ?? true,
      createdBy: brand.createdBy || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isFormDisabled = !canAdd && !editBrand;

  return (
    <div className="p-4 m-0">
      {/* Back */}
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* Form card */}
      <div className="catlist mb-4">
        {editBrand && (
          <h5 className="mb-4" style={{ color: '#6E5199', fontWeight: '600' }}>
            Edit Brand
          </h5>
        )}

        <div className="row g-3">
          <div className="col-lg-3 col-md-6">
            <label>
              Brand Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Brand Name"
              disabled={isFormDisabled}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label>
              Brand Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="Brand Code"
              disabled={isFormDisabled}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label>Created By</label>
            <input
              type="text"
              className="form-control"
              name="createdBy"
              value={formData.createdBy}
              onChange={handleInputChange}
              placeholder="Created By"
              disabled={isFormDisabled}
            />
          </div>

          <div className="col-lg-1 col-md-6 d-flex align-items-end pb-1">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="isActiveSwitch"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                disabled={isFormDisabled}
              />
              <label className="form-check-label ms-2" htmlFor="isActiveSwitch">
                Active
              </label>
            </div>
          </div>

          <div className="col-lg-2 col-md-6 d-flex align-items-end gap-2">
            <button className="addItem-btn" onClick={handleSave} disabled={isFormDisabled} style={{ minWidth: '80px' }}>
              {editBrand ? 'Update' : 'Save'}
            </button>
            {editBrand && (
              <button className="btn btn-secondary" onClick={resetFormData} style={{ minWidth: '80px' }}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grid — full width */}
      <div style={{ width: '100%', padding: '15px', boxShadow: '0 0 10px rgba(0,0,0,0.1)', borderRadius: '8px', margintop: '50px' }}>
        <BrandMasterGrid rowData={brands} canEditMaster={canEdit} onEditBrand={handleEditBrand} fetchBrandData={getBrands} />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(BrandMaster);
